import type { BoqRow, DiffField, DiffStatus, MatchResult, MatchType } from '../types'

export const DIFF_STATUS_LABELS: Record<DiffStatus, string> = {
  added: '新增',
  removed: '删除',
  changed: '修改',
  unchanged: '未变化',
  pending: '待确认',
}

export const MATCH_TYPE_LABELS: Record<MatchType, string> = {
  'unique-code': '唯一编码',
  'duplicate-code': '重复编码消歧',
  'exact-features': '特征精确匹配',
  fuzzy: '模糊候选',
  manual: '人工确认',
  unmatched: '未匹配',
}

export const DIFF_FIELD_LABELS: Record<DiffField, string> = {
  itemCode: '项目编码',
  itemName: '项目名称',
  itemDescription: '项目特征',
  unit: '单位',
  quantity: '工程量',
  unitPrice: '综合单价',
  totalPrice: '合价',
}

export function formatMoney(value: number | null, empty = '无法计算'): string {
  if (value === null || !Number.isFinite(value)) return empty
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatNumber(value: number | null, digits = 6, empty = '—'): string {
  if (value === null || !Number.isFinite(value)) return empty
  return new Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: digits,
  }).format(value)
}

export function formatPercentage(value: number | null, reason?: string): string {
  if (value === null || !Number.isFinite(value)) {
    return reason === 'baseline-zero' ? '基准为 0' : '—'
  }
  return `${(value * 100).toFixed(2)}%`
}

export function formatConfidence(value: number): string {
  return `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%`
}

export function primaryRow(result: MatchResult): BoqRow | null {
  return result.targetRow ?? result.baselineRow
}

export function secondaryRow(result: MatchResult): BoqRow | null {
  return result.baselineRow ?? result.targetRow
}

export function sourceRowLabel(result: MatchResult): string {
  const baseline = result.baselineRow?.sourceRowNumber ?? '—'
  const target = result.targetRow?.sourceRowNumber ?? '—'
  return `${baseline} / ${target}`
}

export function changedFieldCount(result: MatchResult): number {
  return Object.values(result.fieldChanges).filter((change) => change.changed).length
}

export function resultSearchText(result: MatchResult): string {
  const rows = [
    result.baselineRow,
    result.targetRow,
    ...result.candidates.map((item) => item.targetRow),
  ]
  return rows
    .filter((row): row is BoqRow => row !== null)
    .flatMap((row) => [row.itemCode, row.itemName, row.itemDescription, row.unit])
    .join(' ')
    .toLocaleLowerCase('zh-CN')
}
